/**
 * Native TTS wrapper for Capacitor (Android/iOS).
 * Falls back to Web Speech API on web platforms.
 */
import { TextToSpeech } from '@capacitor-community/text-to-speech';
import { Capacitor } from '@capacitor/core';

const isNative = Capacitor.isNativePlatform();

export function getSelectedVoice() {
  if (typeof window === 'undefined' || !window.speechSynthesis) return null;
  try {
    const uri = localStorage.getItem('cogency_output_voice_uri');
    if (!uri) return null;
    return window.speechSynthesis.getVoices().find(v => v.voiceURI === uri) || null;
  } catch {
    return null;
  }
}

export function getAgentVoice(agentNameOrId) {
  if (typeof window === 'undefined' || !window.speechSynthesis) return null;
  try {
    const raw = localStorage.getItem('cogency_agent_voice_uris');
    if (!raw) return null;
    const map = JSON.parse(raw);
    const uri = map[agentNameOrId] || map[String(agentNameOrId).toLowerCase()] || null;
    if (!uri) return null;
    return window.speechSynthesis.getVoices().find(v => v.voiceURI === uri) || null;
  } catch {
    return null;
  }
}

export function getVoicePreferences() {
  try {
    const rate = localStorage.getItem('cogency_output_voice_rate');
    const pitch = localStorage.getItem('cogency_output_voice_pitch');
    return {
      rate: rate ? parseFloat(rate) : 1,
      pitch: pitch ? parseFloat(pitch) : 1,
    };
  } catch {
    return { rate: 1, pitch: 1 };
  }
}

export async function speakNative({ text, lang = 'fr-FR', rate = 1.0, pitch = 1.0, voice = null, agentName = null, onStart, onEnd }) {
  if (!text) {
    onEnd?.();
    return;
  }

  const prefs = getVoicePreferences();
  const finalRate = rate ?? prefs.rate ?? 1;
  const finalPitch = pitch ?? prefs.pitch ?? 1;
  const selectedVoice = voice || getAgentVoice(agentName) || getSelectedVoice();

  if (isNative) {
    try {
      onStart?.();
      await TextToSpeech.speak({
        text: String(text).slice(0, 5000),
        lang: selectedVoice?.lang || lang,
        rate: finalRate,
        pitch: finalPitch,
        volume: 1.0,
        category: 'playback',
      });
      onEnd?.();
      return;
    } catch (err) {
      console.error('Native TTS speak error:', err);
      onEnd?.();
      return;
    }
  }

  // Web fallback: use speechSynthesis
  if (!window.speechSynthesis) {
    onEnd?.();
    return;
  }

  window.speechSynthesis.cancel();
  const utter = new SpeechSynthesisUtterance(text);
  if (selectedVoice) {
    utter.voice = selectedVoice;
    utter.lang = selectedVoice.lang || lang;
  } else {
    utter.lang = lang;
  }
  utter.rate = finalRate;
  utter.pitch = finalPitch;
  utter.onstart = () => onStart?.();
  utter.onend = () => onEnd?.();
  utter.onerror = () => onEnd?.();
  window.speechSynthesis.speak(utter);
}

export async function stopNativeTTS() {
  if (isNative) {
    try {
      await TextToSpeech.stop();
    } catch {}
  }
  try {
    window.speechSynthesis?.cancel();
  } catch {}
}
